import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Star,
  Save,
  Zap
} from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '@/config/api';

const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface SkillsTabProps {
  data: any;
  loading: boolean;
  isEditable: boolean;
  editMode: boolean;
  onEdit: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  candidateUuid?: string | null;
  onRefresh?: () => void;
}

const SkillsTab: React.FC<SkillsTabProps> = ({ 
  data, 
  loading, 
  isEditable, 
  editMode, 
  onEdit, 
  onSave, 
  onCancel,
  candidateUuid,
  onRefresh
}) => {
  const [editData, setEditData] = useState(data);
  const updateEditData = (updater: (prev: any) => any) => setEditData((prev: any) => updater(prev));
  const [showAddSkillForm, setShowAddSkillForm] = useState(false);
  const [newSkill, setNewSkill] = useState<{ skillName: string; skillCategory: string; isPrimarySkill: boolean; yearsOfExperience: number; rating: number }>({
    skillName: '',
    skillCategory: 'Technical',
    isPrimarySkill: false,
    yearsOfExperience: 0,
    rating: 1
  });

  useEffect(() => {
    setEditData(data);
  }, [data]);

  // Debug logging
  console.log('🔧 SkillsTab render:', {
    isEditable,
    hasData: !!data,
    skillsCount: data?.skills?.length || 0,
    editMode,
    loading
  });

  if (loading) {
    return <Card><CardContent className="p-6">Loading skills...</CardContent></Card>;
  }

  const handleSave = () => {
    onSave(editData);
  };

  const handleCancel = () => {
    setEditData(data);
    setShowAddSkillForm(false);
    setNewSkill({
      skillName: '',
      skillCategory: 'Technical',
      isPrimarySkill: false,
      yearsOfExperience: 0,
      rating: 1
    });
    onCancel();
  };

  const handleSkillChange = (skillUuid: string, field: string, value: any) => {
    updateEditData((prev: any) => ({
      ...prev,
      skills: prev?.skills?.map((skill: any) => 
        skill.uuid === skillUuid ? { ...skill, [field]: value } : skill
      ) || [],
      skills_by_category: prev?.skills_by_category ? 
        Object.fromEntries(
          (Object.entries(prev.skills_by_category as any) as Array<[string, any[]]>).map(([category, categorySkills]: [string, any[]]) => [
            category,
            categorySkills.map((skill: any) => 
              skill.uuid === skillUuid ? { ...skill, [field]: value } : skill
            )
          ])
        ) : {},
      primary_skills: prev?.primary_skills?.map((skill: any) => 
        skill.uuid === skillUuid ? { ...skill, [field]: value } : skill
      ) || []
    }));
  };

  const handleDeleteSkill = async (skillUuid: string, skillName: string) => {
    if (!window.confirm(`Are you sure you want to delete the skill "${skillName}"?`)) {
      return;
    }

    try {
      console.log('🔧 Starting skill deletion process...');
      console.log('🔧 Skill UUID:', skillUuid);
      
      const url = buildApiUrl(`profile-tabs/me/profile/skills/${skillUuid}`);
      const headers = getApiHeaders(true);
      
      console.log('🔧 Delete URL:', url);
      console.log('🔧 Headers:', headers);

      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      console.log('🔧 Delete response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔧 Delete response data:', result);

      if (result.success) {
        toast.success(result.message || 'Skill deleted successfully');
        
        if (onRefresh) {
          console.log('🔧 Calling onRefresh callback after deletion');
          onRefresh();
        } else {
          console.log('🔧 No onRefresh callback, reloading page');
          window.location.reload();
        }
      } else {
        console.log('🔧 Delete API returned unsuccessful response:', result);
        toast.error(result.message || 'Failed to delete skill');
      }
    } catch (error: any) {
      console.error('🔧 Detailed error in handleDeleteSkill:', error);
      console.error('🔧 Error message:', (error as any)?.message);
      toast.error(`Error deleting skill: ${(error as any)?.message}`);
    }
  };

  const handleAddNewSkill = async () => {
    if (!newSkill.skillName.trim()) {
      toast.error('Skill name is required');
      return;
    }
    
    if (!newSkill.rating || newSkill.rating < 1 || newSkill.rating > 10) {
      toast.error('Rating must be between 1 and 10');
      return;
    }

    try {
      console.log('🔧 Starting skill addition process...');
      console.log('🔧 New skill data:', newSkill);
      
      const url = buildApiUrl('profile-tabs/me/profile/skills');
      const headers = getApiHeaders(true);
      
      // Map the form data to the expected API format
      const skillPayload = {
        skillName: newSkill.skillName,
        skillCategory: newSkill.skillCategory,
        isPrimarySkill: newSkill.isPrimarySkill,
        proficiencyLevel: 'intermediate',
        yearsOfExperience: newSkill.yearsOfExperience,
        rating: newSkill.rating,
        certificationRelated: false
      };
      
      console.log('🔧 API URL:', url);
      console.log('🔧 Headers:', headers);
      console.log('🔧 Request body:', JSON.stringify(skillPayload));

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(skillPayload)
      });

      console.log('🔧 Response status:', response.status);
      console.log('🔧 Response headers:', Array.from(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔧 Response data:', result);

      if (result.success) {
        toast.success('Skill added successfully');
        setShowAddSkillForm(false);
        setNewSkill({
          skillName: '',
          skillCategory: 'Technical',
          isPrimarySkill: false,
          yearsOfExperience: 0,
          rating: 1
        });
        
        if (onRefresh) {
          console.log('🔧 Calling onRefresh callback');
          onRefresh();
        } else {
          console.log('🔧 No onRefresh callback, reloading page');
          window.location.reload();
        }
      } else {
        console.log('🔧 API returned unsuccessful response:', result);
        toast.error(result.message || 'Failed to add skill');
      }
    } catch (error: any) {
      console.error('🔧 Detailed error in handleAddNewSkill:', error);
      console.error('🔧 Error name:', (error as any)?.name);
      console.error('🔧 Error message:', (error as any)?.message);
      console.error('🔧 Error stack:', (error as any)?.stack);
      toast.error(`Error adding skill: ${(error as any)?.message}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Skills ({data?.total_count || 0})
        </CardTitle>
        {!editMode && (
          <div className="flex gap-2">
            {isEditable && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowAddSkillForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Skill
                </Button>
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {showAddSkillForm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium mb-3">Add New Skill</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skill Name *</label>
                <input
                  type="text"
                  value={newSkill.skillName}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, skillName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., React, Python, AWS"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newSkill.skillCategory}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, skillCategory: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Technical">Technical</option>
                  <option value="Programming Languages">Programming Languages</option>
                  <option value="Frameworks">Frameworks</option>
                  <option value="Databases">Databases</option>
                  <option value="Cloud Platforms">Cloud Platforms</option>
                  <option value="Tools">Tools</option>
                  <option value="Soft Skills">Soft Skills</option>
                  <option value="Languages">Languages</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating (1-10)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={newSkill.rating}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, rating: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Rate your skill level (1-10)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={newSkill.yearsOfExperience}
                  onChange={(e) => setNewSkill(prev => ({ ...prev, yearsOfExperience: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newSkill.isPrimarySkill}
                    onChange={(e) => setNewSkill(prev => ({ ...prev, isPrimarySkill: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Primary Skill</span>
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddNewSkill} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Skill
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddSkillForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {data?.skills && data.skills.length > 0 ? (
          <div className="space-y-6">
            {data?.primary_skills && data.primary_skills.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  Primary Skills
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(editMode ? (editData?.primary_skills || []) : data.primary_skills).map((skill: any, index: number) => (
                        <div key={skill.uuid || index} className="flex items-center gap-1">
                          {editMode ? (
                            <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 space-y-2">
                              <input
                                type="text"
                                value={skill.skillName || ''}
                                onChange={(e) => handleSkillChange(skill.uuid, 'skillName', e.target.value)}
                                className="w-full bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none text-sm font-medium"
                                placeholder="Skill name"
                              />
                              <div className="flex gap-2">
                                <select
                                  value={skill.skillCategory || 'Technical'}
                                  onChange={(e) => handleSkillChange(skill.uuid, 'skillCategory', e.target.value)}
                                  className="text-xs bg-white dark:bg-gray-900 dark:text-gray-100 border rounded px-1 py-0.5 flex-1"
                                >
                                  <option value="Technical">Technical</option>
                                  <option value="Programming Languages">Programming</option>
                                  <option value="Frameworks">Frameworks</option>
                                  <option value="Databases">Databases</option>
                                  <option value="Cloud Platforms">Cloud</option>
                                  <option value="Tools">Tools</option>
                                  <option value="Soft Skills">Soft Skills</option>
                                  <option value="Languages">Languages</option>
                                  <option value="Other">Other</option>
                                </select>
                                <select
                                  value={skill.proficiencyLevel || 'intermediate'}
                                  onChange={(e) => handleSkillChange(skill.uuid, 'proficiencyLevel', e.target.value)}
                                  className="text-xs bg-white dark:bg-gray-900 dark:text-gray-100 border rounded px-1 py-0.5"
                                >
                                  <option value="beginner">Beginner</option>
                                  <option value="intermediate">Intermediate</option>
                                  <option value="advanced">Advanced</option>
                                  <option value="expert">Expert</option>
                                </select>
                                <input
                                  type="number"
                                  value={skill.yearsOfExperience || 0}
                                  onChange={(e) => handleSkillChange(skill.uuid, 'yearsOfExperience', parseInt(e.target.value) || 0)}
                                  className="text-xs bg-white dark:bg-gray-900 dark:text-gray-100 border rounded px-1 py-0.5 w-12"
                                  placeholder="Yrs"
                                  min="0"
                                  max="50"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <label className="flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={skill.isPrimarySkill || false}
                                    onChange={(e) => handleSkillChange(skill.uuid, 'isPrimarySkill', e.target.checked)}
                                    className="rounded text-yellow-500 focus:ring-yellow-500"
                                  />
                                  <span className="text-xs text-gray-700">Primary</span>
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 group">
                              <Badge variant="default" className="px-3 py-1">
                                {skill.skillName} ({skill.proficiencyLevel})
                              </Badge>
                              {isEditable && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      onEdit && onEdit();
                                    }}
                                    className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-1 h-6 w-6"
                                    title={`Edit ${skill.skillName}`}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      console.log('🗑️ Delete button clicked for skill:', skill.skillName, 'UUID:', skill.uuid);
                                      handleDeleteSkill(skill.uuid, skill.skillName);
                                    }}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-6 w-6 border border-red-200"
                                    title={`Delete ${skill.skillName}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                </div>
              </div>
            )}

            {data?.skills_by_category && Object.keys(data.skills_by_category).length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold">All Skills by Category</h3>
                {(Object.entries(editMode ? (editData?.skills_by_category || {}) : data.skills_by_category) as Array<[string, any[]]>).map(([category, skills]: [string, any[]]) => (
                  <div key={category} className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3 text-sm uppercase tracking-wide text-gray-600">
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {skills.map((skill: any, index: number) => (
                        <div key={skill.uuid || index} className={`p-2 rounded ${editMode ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                          {editMode ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={skill.skillName || ''}
                                onChange={(e) => handleSkillChange(skill.uuid, 'skillName', e.target.value)}
                                className="w-full bg-transparent border-b border-gray-300 focus:border-blue-500 outline-none font-medium text-sm"
                                placeholder="Skill name"
                              />
                              <div className="flex gap-1">
                                <select
                                  value={skill.skillCategory || 'Technical'}
                                  onChange={(e) => handleSkillChange(skill.uuid, 'skillCategory', e.target.value)}
                                  className="text-xs bg-white border rounded px-1 py-0.5 flex-1"
                                >
                                  <option value="Technical">Technical</option>
                                  <option value="Programming Languages">Programming</option>
                                  <option value="Frameworks">Frameworks</option>
                                  <option value="Databases">Databases</option>
                                  <option value="Cloud Platforms">Cloud</option>
                                  <option value="Tools">Tools</option>
                                  <option value="Soft Skills">Soft Skills</option>
                                  <option value="Languages">Languages</option>
                                  <option value="Other">Other</option>
                                </select>
                                <select
                                  value={skill.proficiencyLevel || 'intermediate'}
                                  onChange={(e) => handleSkillChange(skill.uuid, 'proficiencyLevel', e.target.value)}
                                  className="text-xs bg-white border rounded px-1 py-0.5"
                                >
                                  <option value="beginner">Beginner</option>
                                  <option value="intermediate">Intermediate</option>
                                  <option value="advanced">Advanced</option>
                                  <option value="expert">Expert</option>
                                </select>
                                <input
                                  type="number"
                                  value={skill.yearsOfExperience || 0}
                                  onChange={(e) => handleSkillChange(skill.uuid, 'yearsOfExperience', parseInt(e.target.value) || 0)}
                                  className="text-xs bg-white border rounded px-1 py-0.5 w-12"
                                  placeholder="Yrs"
                                  min="0"
                                  max="50"
                                />
                              </div>
                              <div className="flex items-center">
                                <label className="flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={skill.isPrimarySkill || false}
                                    onChange={(e) => handleSkillChange(skill.uuid, 'isPrimarySkill', e.target.checked)}
                                    className="rounded text-yellow-500 focus:ring-yellow-500"
                                  />
                                  <span className="text-xs text-gray-700">Primary</span>
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between group">
                              <div className="flex-1">
                                <div className="font-medium text-sm">{skill.skillName}</div>
                                <div className="text-xs text-gray-500">
                                  {skill.yearsOfExperience} years • {skill.proficiencyLevel}
                                </div>
                                {skill.lastUsedDate && (
                                  <div className="text-xs text-gray-400">
                                    Last used: {skill.lastUsedDate}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {skill.assessmentScore && (
                                  <Badge variant="outline" className="text-xs">
                                    {skill.assessmentScore}%
                                  </Badge>
                                )}
                                {isEditable && (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        onEdit && onEdit();
                                      }}
                                      className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-1 h-6 w-6"
                                      title={`Edit ${skill.skillName}`}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        console.log('🗑️ Delete button clicked for skill:', skill.skillName, 'UUID:', skill.uuid);
                                        handleDeleteSkill(skill.uuid, skill.skillName);
                                      }}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-6 w-6 border border-red-200"
                                      title={`Delete ${skill.skillName}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-500">No skills information available</p>
        )}
        
        {editMode && isEditable && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SkillsTab;
